<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { DocumentTextIcon, ArrowTopRightOnSquareIcon, Cog6ToothIcon, XMarkIcon } from '@heroicons/vue/24/outline'

import { mainNavigation, configNavigation } from '@/utils/defaults'
import { useAccount } from '@/stores/account'
import { useAuth } from '@/stores/auth'

const account = useAccount()
const auth = useAuth()
const route = useRoute()
const router = useRouter()

const props = defineProps({
  showSideBar: {
    type: Boolean,
  },
})

const emit = defineEmits(['toggle'])

const filteredConfigNavigation = computed(() => configNavigation.filter((item) => item.name !== 'Migrations' || account.groups?.[0] === 'appsumo_tier5'))

function isActive(href) {
  return route.path.startsWith(href)
}

function toggleShow(value) {
  emit('toggle', value)
}

function handleNavClick() {
  toggleShow(false)
}

function logout() {
  auth.logout()
  if (typeof $crisp !== 'undefined') {
    $crisp.push(['do', 'chat:hide'])
  }
  router.push({ name: 'login' })
}

const bottomNavItems = [
  { name: 'Settings', href: '/settings', icon: Cog6ToothIcon, external: false },
  { name: 'Documentation', href: '/docs', icon: DocumentTextIcon, external: false },
  { name: 'Feature Request', href: 'https://emailpref.featurebase.app', icon: ArrowTopRightOnSquareIcon, external: true },
]
</script>

<template>
  <!-- Mobile sidebar -->
  <TransitionRoot as="template" :show="props.showSideBar">
    <Dialog as="div" class="relative z-50 xl:hidden">
      <TransitionChild
        as="template"
        enter="transition-opacity ease-linear duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="transition-opacity ease-linear duration-300"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900/80" />
      </TransitionChild>

      <div class="fixed inset-0 flex">
        <TransitionChild
          as="template"
          enter="transition ease-in-out duration-300 transform"
          enter-from="-translate-x-full"
          enter-to="translate-x-0"
          leave="transition ease-in-out duration-300 transform"
          leave-from="translate-x-0"
          leave-to="-translate-x-full"
        >
          <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
            <TransitionChild
              as="template"
              enter="ease-in-out duration-300"
              enter-from="opacity-0"
              enter-to="opacity-100"
              leave="ease-in-out duration-300"
              leave-from="opacity-100"
              leave-to="opacity-0"
            >
              <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button type="button" class="-m-2.5 p-2.5" @click="toggleShow(false)">
                  <span class="sr-only">Close sidebar</span>
                  <XMarkIcon class="h-6 w-6 text-white" aria-hidden="true" />
                </button>
              </div>
            </TransitionChild>
            <div class="flex grow flex-col overflow-y-auto bg-white px-4 pb-4">
              <div class="flex h-16 shrink-0 items-center px-2">
                <img class="h-8 w-auto" src="/src/assets/img/logo-light.png" alt="Emailpref" />
              </div>

              <nav class="flex flex-1 flex-col">
                <!-- Main nav -->
                <ul role="list" class="space-y-1">
                  <li v-for="item in mainNavigation" :key="item.name">
                    <router-link
                      :to="item.href"
                      :class="[
                        isActive(item.href) ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                        'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 transition-colors',
                      ]"
                      @click="handleNavClick"
                    >
                      <component
                        :is="item.icon"
                        :class="[isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600', 'h-5 w-5 shrink-0']"
                        aria-hidden="true"
                      />
                      {{ item.name }}
                    </router-link>
                  </li>
                </ul>

                <!-- Configure section -->
                <div class="mt-6">
                  <p class="text-[11px] font-semibold tracking-wider text-gray-400 uppercase px-3 mb-1">Configure</p>
                  <ul role="list" class="space-y-1">
                    <li v-for="item in filteredConfigNavigation" :key="item.name">
                      <router-link
                        :to="item.href"
                        :class="[
                          isActive(item.href) ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                          'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 transition-colors',
                        ]"
                        @click="handleNavClick"
                      >
                        <component
                          :is="item.icon"
                          :class="[isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600', 'h-5 w-5 shrink-0']"
                          aria-hidden="true"
                        />
                        {{ item.name }}
                      </router-link>
                    </li>
                  </ul>
                </div>

                <!-- Spacer -->
                <div class="flex-1" />

                <!-- Bottom nav -->
                <ul role="list" class="space-y-1 mb-3">
                  <li v-for="item in bottomNavItems" :key="item.name">
                    <component
                      :is="item.external ? 'a' : 'router-link'"
                      v-bind="item.external ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : { to: item.href }"
                      :class="[
                        !item.external && isActive(item.href)
                          ? 'bg-primary/10 text-primary font-semibold'
                          : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                        'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 transition-colors',
                      ]"
                      @click="handleNavClick"
                    >
                      <component
                        :is="item.icon"
                        :class="[!item.external && isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600', 'h-5 w-5 shrink-0']"
                        aria-hidden="true"
                      />
                      {{ item.name }}
                    </component>
                  </li>
                </ul>

                <!-- User section -->
                <div class="border-t border-gray-100 pt-3">
                  <div class="flex items-center gap-x-3 px-3">
                    <span class="inline-block h-6 w-6 overflow-hidden rounded-full bg-gray-100 shrink-0">
                      <img v-if="account.avatar" :src="account.avatar" class="h-full w-full" alt="Avatar" />
                      <svg v-else class="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </span>
                    <span class="text-sm font-medium text-gray-900 truncate">{{ account.username }}</span>
                    <button class="ml-auto text-sm text-gray-500 hover:text-gray-700 transition-colors" @click="logout">Sign out</button>
                  </div>
                </div>
              </nav>
            </div>
          </DialogPanel>
        </TransitionChild>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Desktop sidebar -->
  <div class="flex grow flex-col overflow-y-auto bg-white px-4 pb-4 border-r border-gray-100">
    <div class="flex h-16 shrink-0 items-center px-2">
      <img class="h-8 w-auto" src="/src/assets/img/logo-light.png" alt="Emailpref" />
    </div>

    <nav class="flex flex-1 flex-col">
      <!-- Main nav -->
      <ul role="list" class="space-y-1">
        <li v-for="item in mainNavigation" :key="item.name">
          <router-link
            :to="item.href"
            :class="[
              isActive(item.href) ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
              'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 transition-colors',
            ]"
          >
            <component
              :is="item.icon"
              :class="[isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600', 'h-5 w-5 shrink-0']"
              aria-hidden="true"
            />
            {{ item.name }}
          </router-link>
        </li>
      </ul>

      <!-- Configure section -->
      <div class="mt-6">
        <p class="text-[11px] font-semibold tracking-wider text-gray-400 uppercase px-3 mb-1">Configure</p>
        <ul role="list" class="space-y-1">
          <li v-for="item in filteredConfigNavigation" :key="item.name">
            <router-link
              :to="item.href"
              :class="[
                isActive(item.href) ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 transition-colors',
              ]"
            >
              <component
                :is="item.icon"
                :class="[isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600', 'h-5 w-5 shrink-0']"
                aria-hidden="true"
              />
              {{ item.name }}
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Spacer -->
      <div class="flex-1" />

      <!-- Bottom nav -->
      <ul role="list" class="space-y-1 mb-3">
        <li v-for="item in bottomNavItems" :key="item.name">
          <component
            :is="item.external ? 'a' : 'router-link'"
            v-bind="item.external ? { href: item.href, target: '_blank', rel: 'noopener noreferrer' } : { to: item.href }"
            :class="[
              !item.external && isActive(item.href) ? 'bg-primary/10 text-primary font-semibold' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
              'group flex gap-x-3 rounded-lg px-3 py-2 text-sm leading-6 transition-colors',
            ]"
          >
            <component
              :is="item.icon"
              :class="[!item.external && isActive(item.href) ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600', 'h-5 w-5 shrink-0']"
              aria-hidden="true"
            />
            {{ item.name }}
          </component>
        </li>
      </ul>

      <!-- User section -->
      <div class="border-t border-gray-100 pt-3">
        <div class="flex items-center gap-x-3 px-3">
          <span class="inline-block h-6 w-6 overflow-hidden rounded-full bg-gray-100 shrink-0">
            <img v-if="account.avatar" :src="account.avatar" class="h-full w-full" alt="Avatar" />
            <svg v-else class="h-full w-full text-gray-300" fill="currentColor" viewBox="0 0 24 24">
              <path d="M24 20.993V24H0v-2.996A14.977 14.977 0 0112.004 15c4.904 0 9.26 2.354 11.996 5.993zM16.002 8.999a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </span>
          <span class="text-sm font-medium text-gray-900 truncate">{{ account.username }}</span>
          <button class="ml-auto text-sm text-gray-500 hover:text-gray-700 transition-colors" @click="logout">Sign out</button>
        </div>
      </div>
    </nav>
  </div>
</template>
